import { useReducer, useEffect, RefObject } from 'react'

export const useShow = () => {
  return useReducer((x) => x + 1, 0)
}

export const useEscapeKey = (callback: (e: KeyboardEvent) => void) => useDetectKeyDown(['Escape'], callback)

type Modifier = 'ctrlKey' | 'shiftKey' | 'metaKey' | 'altKey'
/**
 * For use when you need a key down handler, but something keeps hijacking the event from a handler
 * passed down through JSX.
 *
 * @param {string[]} keys
 * @param {(KeyboardEvent) => void} callback
 * @param {string} [modifier] ctrlKey, shiftKey, metaKey, altKey
 */
export const useDetectKeyDown = (
  keys: string[],
  callback: (e: KeyboardEvent) => void,
  modifier?: Modifier,
  options?: boolean | AddEventListenerOptions,
) => {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (modifier) {
        if (keys.includes(event.key) && event[modifier]) {
          callback(event)
        }
      } else {
        if (keys.includes(event.key)) {
          callback(event)
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown, options)
    return () => document.removeEventListener('keydown', handleKeyDown, options)
  }, [keys, options, callback, modifier])
}

/**
 *
 * @param {RefType} ref React ref of the element we're detecting clicks outside of.
 * @param {(MouseEvent) => void} callback Function to be called on outside click.
 * @param {boolean} [condition] Optional boolean that determines whether the provided callback is invoked. Defaults to true.
 */
export const useDetectOutsideClick = (
  ref: RefObject<HTMLElement>,
  callback: (e: MouseEvent) => void,
  condition: boolean = true,
) => {
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (condition && ref.current && !ref.current.contains(event.target as HTMLElement)) {
        callback(event)
      }
    }

    document.addEventListener('mousedown', handleClickOutside, { capture: true })
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [ref, callback, condition])
}
